import { useQuery } from '@apollo/client';
import {
  ContentDeciderContainer,
  getTranslatedText,
  Loader,
  getParsedCrudData,
  ALL_SHARD_MODULES
} from '@nile/components';
import envConfig from '@nile/home/src/Config/env';
import { GET_FIELD, AccountClient, useCachedQuery } from '@nile/graphql';
import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { cloneDeep, isEmpty, pick, sortBy } from 'lodash';
import CascadeChild from './CascadeChild';
import { useFlags } from 'launchdarkly-react-client-sdk';

const CascadeContainer = (props) => {
  const { intl, disableSort } = props;

  const flags = useFlags();
  const isNewFieldTemplate = flags?.dependentField;

  const [mandatoryOnCreation, setMandatoryOnCreation] = useState(false);
  const [mandatoryOnClosure, setMandatoryOnClosure] = useState(false);

  const { data: fieldData, loading, error } = useQuery(GET_FIELD, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        entity: ALL_SHARD_MODULES.ticket,
        columnName: props.columnName
      }
    },
    onCompleted({ getField }) {
      setMandatoryOnCreation(getField?.mandatoryOnCreate);
      setMandatoryOnClosure(getField?.mandatoryOnClosure);
    }
  });

  const handleSave = (data) => {
    const base =
      fieldData?.getField?.options || fieldData?.getField?.fieldOptions;
    const updatedData = data.map(({ name, ...parentAttrs }) => {
      const children =
        parentAttrs?.children && parentAttrs?.children.length
          ? parentAttrs?.children.map(({ name, ...childAttrs }) => childAttrs)
          : null;
      return {
        ...parentAttrs,
        children: children
      };
    });

    const parentCrudData = getParsedCrudData(base, updatedData);

    let added = {};
    if (parentCrudData?.addedItems.length > 0) {
      const addedParent = parentCrudData?.addedItems.map((el) => {
        const children = el?.children || null;
        let obj = {
          value: el?.value
        };
        if (isNewFieldTemplate) {
          obj = {
            ...obj,
            order: el?.order
          };
        }
        if (children) {
          const childValues = [];
          children.forEach((child) => {
            let childObj = { value: child?.value };
            const description = child?.details?.description;
            if (description) {
              childObj = {
                ...childObj,
                details: {
                  description: description
                }
              };
            }
            childValues.push(childObj);
          });
          obj = {
            ...obj,
            children: {
              added: childValues
            }
          };
        }
        return obj;
      });
      added = { added: addedParent };
    }

    let deleted = {};
    if (parentCrudData?.deletedItems.length > 0) {
      const deletedParent = parentCrudData?.deletedItems.map((el) => ({
        id: el?.id
      }));
      deleted = { deleted: deletedParent };
    }

    let updated = {};
    if (parentCrudData?.updatedItems.length > 0) {
      const updatedParent = parentCrudData?.updatedItems.map((el) => {
        const baseParent = base.find((f) => f.id === el.id);
        const baseChildren = baseParent?.children || [];
        const childData = el?.children || [];
        const childCrudData = getParsedCrudData(baseChildren, childData);

        let addedChildren = {};
        let updatedChildren = {};
        let deletedChildren = {};

        if (childCrudData?.addedItems?.length > 0) {
          const addedChild = childCrudData?.addedItems.map((ch) => {
            const description = ch?.details?.description || null;
            let obj = {
              value: ch?.value
            };
            if (isNewFieldTemplate) {
              obj = {
                ...obj,
                order: ch?.order
              };
            }
            if (description) {
              obj = {
                ...obj,
                details: {
                  description: description
                }
              };
            }
            return obj;
          });
          addedChildren = { added: addedChild };
        }

        if (childCrudData?.deletedItems.length > 0) {
          const deletedChild = childCrudData?.deletedItems.map((ch) => ({
            id: ch?.id
          }));
          deletedChildren = { deleted: deletedChild };
        }

        if (childCrudData?.updatedItems.length > 0) {
          const updatedChild = childCrudData?.updatedItems.map((ch) => {
            const description = ch?.details?.description || null;
            let obj = {
              id: ch?.id,
              value: ch?.value,
              order: ch?.order
            };
            if (description) {
              obj = {
                ...obj,
                details: {
                  description: description
                }
              };
            }
            return obj;
          });
          updatedChildren = { updated: updatedChild };
        }

        const childrenValues = {
          ...addedChildren,
          ...deletedChildren,
          ...updatedChildren
        };

        let obj = {
          id: el?.id,
          value: el?.value,
          order: el?.order
        };
        if (!isEmpty(childrenValues)) {
          obj = {
            ...obj,
            children: childrenValues
          };
        }

        return obj;
      });

      updated = { updated: updatedParent };
    }

    const fieldOptionsInput = {
      ...added,
      ...deleted,
      ...updated
    };

    let variables = {
      id: fieldData?.getField?.id,
      mandatoryOnCreate: mandatoryOnCreation,
      mandatoryOnClosure: mandatoryOnClosure
    };

    if (!isEmpty(fieldOptionsInput)) {
      if (isNewFieldTemplate) {
        variables = {
          ...variables,
          addOptions: fieldOptionsInput?.added?.map((el) =>
            pick(el, ['value', 'description', 'order', 'parentOption'])
          ),
          updateOptions: fieldOptionsInput?.updated?.map((el) =>
            pick(el, ['value', 'description', 'order', 'id'])
          ),
          deleteOptions: fieldOptionsInput?.deleted?.map((el) =>
            pick(el, ['id'])
          )
        };
      } else {
        variables = {
          ...variables,
          fieldOptionsInput: JSON.stringify(fieldOptionsInput)
        };
      }
    }

    props.primaryAction(variables);
  };

  if (loading) {
    return <Loader />;
  }
  const getSortedOptions = (data) => {
    if (!isEmpty(data)) {
      const clonedData = cloneDeep(data);
      let sortedData = sortBy(clonedData, 'order');
      sortedData = sortedData.map((el) => {
        const sortedChild = !isEmpty(el?.children)
          ? sortBy(el?.children, 'order')
          : [];
        const children = sortedChild.map((ch) => ({
          ...ch,
          name: ch?.value
        }));
        return {
          ...el,
          name: el?.value,
          children: children
        };
      });
      return sortedData;
    }
    return null;
  };

  const options = getSortedOptions(
    fieldData?.getField?.options || fieldData?.getField?.fieldOptions
  );

  return (
    <ContentDeciderContainer
      isLoading={loading}
      hasContent
      mainContent={
        <>
          <CascadeChild
            options={options || null}
            mandatoryOnCreation={mandatoryOnCreation}
            mandatoryOnClosure={mandatoryOnClosure}
            setMandatoryOnCreation={setMandatoryOnCreation}
            setMandatoryOnClosure={setMandatoryOnClosure}
            secondaryAction={props.secondaryAction}
            showWarning={props.showWarning}
            columnName={props.columnName}
            handleSave={handleSave}
            title={props.title}
            subtitle={props.subtitle}
            featureGuide={props.featureGuide}
            parentTitle={props.parentTitle}
            childTitle={props.childTitle}
            parentPlaceholderText={props.parentPlaceholderText}
            childrenPlaceholderText={props.childrenPlaceholderText}
            loading={loading}
            saveLoading={props.saveLoading}
            isEditAuthorised={props.isEditAuthorised}
            disableSort={disableSort}
            isNewFieldTemplate={isNewFieldTemplate}
          />
        </>
      }
      fullHeight
      notFoundURL={envConfig.imageRoot + 'svg/not-found.svg'}
      heading={getTranslatedText(intl, 'admin.causes.emptyState')}
    />
  );
};
export default injectIntl(CascadeContainer);
